import type { CandidateOrigin } from '@/core/conversations/conversation-index/candidate-origin.enum';
import { getCandidateOrigin } from '@/core/conversations/conversation-index/candidate-origin.util';
import type {
  ApplicationIndex,
  ConversationIndex,
} from '@/core/conversations/conversation-index/conversation-index.type';

/**
 * The origin list to fetch a deep-linked conversation with. A deep link is an explicit
 * request for one candidate, so when the active origin filter would exclude that
 * candidate the filter widens to all origins rather than rendering a row that
 * contradicts it (ONE-7281).
 */
export function originsForDeepLinkedConversation(
  origins: CandidateOrigin[],
  application: Partial<ApplicationIndex> | undefined,
): CandidateOrigin[] {
  if (origins.length === 0 || origins.includes(getCandidateOrigin(application))) {
    return origins;
  }

  return [];
}

/**
 * The row to insert for a deep-linked conversation, with `primaryApplicationIndex` pinned
 * to its application for the job being viewed. The by-id fetches return every application
 * the candidate has and the primary is chosen across jobs, so the card would otherwise
 * render another job's application — including an origin chip contradicting both the
 * active filter and the widening decision above, which is keyed to this job (ONE-7281).
 */
export function withPrimaryApplicationForJob(
  conversation: ConversationIndex,
  jobId: number,
): ConversationIndex {
  const applicationForJob = conversation.applicationIndexes?.find((app) => app.jobId === jobId);

  if (
    !applicationForJob ||
    applicationForJob.applicationId === conversation.primaryApplicationIndex?.applicationId
  ) {
    return conversation;
  }

  return { ...conversation, primaryApplicationIndex: applicationForJob };
}
